import { NgModule } from '@angular/core';
import { BrowserModule, provideClientHydration, withEventReplay, withNoIncrementalHydration } from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS, withXhr } from '@angular/common/http';
import {
  MsalModule,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  BrowserCacheLocation,
} from '@azure/msal-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import {
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MsalInterceptor,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import { InteractionType } from '@azure/msal-browser';
import { MockJwtInterceptor } from './core/interceptors/mock-jwt.interceptor';
import { AdminScopeInterceptor } from './core/interceptors/admin-scope.interceptor';

/**
 * MSAL Instance factory
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: `https://login.microsoftonline.com/${environment.msal.tenantId}`,
      redirectUri: environment.msal.redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false,
    },
  });
}

@NgModule({
  imports: [
    BrowserModule,
    AppRoutingModule,
    MsalModule,
    AppComponent // Import standalone AppComponent
  ],
  providers: [
    provideClientHydration(withEventReplay(), withNoIncrementalHydration()),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: environment.msal.scopes.read,
        },
      },
    },
    // Only configure MSAL interceptor and resource map when auth is enabled
    ...(!environment.noAuthMode ? [
      {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useValue: {
          interactionType: InteractionType.Redirect,
          protectedResourceMap: new Map([
            // Only configure read scopes for default API endpoints
            // AdminScopeInterceptor handles admin endpoints with Write scopes
            [environment.msal.protectedResources.metricsApi.endpoint, environment.msal.protectedResources.metricsApi.scopes],
          ]),
        },
      },
      {
        // AdminScopeInterceptor runs first to handle admin endpoints with Write scope
        provide: HTTP_INTERCEPTORS,
        useClass: AdminScopeInterceptor,
        multi: true,
      },
      {
        // MsalInterceptor handles non-admin endpoints with Read scope
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true,
      },
    ] : []),
    // When auth is disabled, attach static bearer via mock interceptor
    ...(environment.noAuthMode ? [
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MockJwtInterceptor,
        multi: true,
      },
    ] : []),
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
